import React, { useEffect, useState } from 'react';
import brand from '../../config/brand';
import { api, preview } from './GroupActivation';
import './groupConversion.css';

type Member = { iccid: string; nickname?: string; sim_type: 'solar' | 'regular' };
type Plan = { name: string; data_gb: number; interval: 'year' | 'month'; unit_amount: number };
type Account = { email: string; members: Member[]; plan: Plan; renewal_at?: number; max_new_sims: number };
type Added = Member & { code: string; confirmed_last4: string };
type Quote = { combined_count: number; combined_data_gb: number; renewal_total: number;
  currency: string; due_now: number | null; renewal_at?: number; plan: Plan };
type ConversionOrder = { id: string; status: string; checkout_url?: string; conversion_due_now?: number;
  items: Array<Member & { state: string; message?: string }> };
const enabled = preview || process.env.REACT_APP_GROUP_RETURNING_CONVERSION_ENABLED === 'true';
const key = preview ? 'farmstream-conversion-preview-v1' : 'farmstream-conversion-v1';
const formatMoney = (amount: number) => new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'GBP', maximumFractionDigits: amount % 100 ? 2 : 0
}).format(amount / 100);

export function GroupConversion() {
  const [account, setAccount] = useState<Account | null>(null);
  const [entries, setEntries] = useState<Added[]>(() => {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
  });
  const [mode, setMode] = useState<'code' | 'iccid'>('code');
  const [code, setCode] = useState('');
  const [candidate, setCandidate] = useState<Added | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [order, setOrder] = useState<ConversionOrder | null>(null);
  const [access, setAccess] = useState<{ orderId: string; token: string } | null>(() => {
    try { return JSON.parse(localStorage.getItem(key + '-order') || 'null'); } catch { return null; }
  });
  const [error, setError] = useState('');
  const [wrongAccount, setWrongAccount] = useState(false);
  const [busy, setBusy] = useState(false);
  const signedIn = preview || Boolean(localStorage.getItem('token'));
  const valid = mode === 'code' ? /^[A-Z]{4}$/.test(code) : /^\d{19,20}$/.test(code);

  useEffect(() => { localStorage.setItem(key, JSON.stringify(entries)); }, [entries]);
  useEffect(() => {
    if (!access) return;
    localStorage.setItem(key + '-order', JSON.stringify(access));
    let stopped = false;
    const refresh = () => api(`/orders/${access.orderId}`, 'GET', undefined, access.token)
      .then((current: ConversionOrder) => { if (!stopped) setOrder(current); })
      .catch(() => { if (!stopped) { setAccess(null); localStorage.removeItem(key + '-order'); } });
    refresh();
    const timer = window.setInterval(refresh, 2500);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [access]);
  useEffect(() => {
    if (!enabled || !signedIn) return;
    let mounted = true;
    api('/conversion/account').then((result: Account) => {
      if (!mounted) return;
      const requested = sessionStorage.getItem('farmstream-group-returning-email');
      if (!preview && requested && result.email.toLowerCase() !== requested.toLowerCase()) {
        setWrongAccount(true);
        setError('You are signed in with a different email. Sign in with the email you entered to add SIMs to that plan.');
        return;
      }
      setAccount(result);
    }).catch((failure: Error) => { if (mounted) setError(failure.message); });
    return () => { mounted = false; };
  }, [signedIn]);

  async function checkSim(event: React.FormEvent) {
    event.preventDefault();
    if (!valid || !account) return;
    setBusy(true); setError('');
    try {
      const sim = await api('/validate', 'POST', { code });
      if (account.members.some(member => member.iccid === sim.iccid)
          || entries.some(member => member.iccid === sim.iccid)) {
        throw new Error('This SIM is already in your plan or list.');
      }
      setCandidate({ ...sim, code, confirmed_last4: sim.iccid.slice(-4) });
      setConfirmed(false);
    } catch (failure) { setError((failure as Error).message); }
    finally { setBusy(false); }
  }

  function addSim() {
    if (!candidate || !confirmed || !account || entries.length >= account.max_new_sims) return;
    setEntries(current => [...current, candidate]);
    setCandidate(null); setCode(''); setConfirmed(false); setQuote(null);
  }

  async function checkPrice() {
    if (!entries.length) return;
    setBusy(true); setError('');
    try {
      const result = await api('/conversion/quote', 'POST', {
        items: entries.map(({ code, confirmed_last4 }) => ({ code, confirmed_last4 }))
      });
      setQuote(result);
    } catch (failure) { setError((failure as Error).message); setQuote(null); }
    finally { setBusy(false); }
  }

  async function simulateCheckout() {
    if (!quote || !preview) return;
    setBusy(true); setError('');
    try {
      const created = await api('/conversion/orders', 'POST', {});
      const orderId = created.order.id as string;
      const token = created.access_token as string;
      const updated = await api(`/conversion/orders/${orderId}`, 'PUT', {
        items: entries.map(({ code, confirmed_last4 }) => ({ code, confirmed_last4 }))
      }, token);
      setOrder(updated);
      setAccess({ orderId, token });
      const checkout = await api(`/conversion/orders/${orderId}/checkout`, 'POST', {}, token);
      setOrder(checkout);
    } catch (failure) { setError((failure as Error).message); }
    finally { setBusy(false); }
  }

  async function simulatePayment(outcome: 'success' | 'decline') {
    if (!access) return;
    setBusy(true); setError('');
    try {
      const updated = await api(`/orders/${access.orderId}/preview-payment`, 'POST', { outcome }, access.token);
      setOrder(updated);
    } catch (failure) { setError((failure as Error).message); }
    finally { setBusy(false); }
  }

  async function backFromPayment() {
    if (!access) return;
    setBusy(true); setError('');
    try {
      await api(`/conversion/orders/${access.orderId}/cancel`, 'POST', {}, access.token);
      localStorage.removeItem(key + '-order');
      setAccess(null); setOrder(null);
    } catch (failure) { setError((failure as Error).message); }
    finally { setBusy(false); }
  }

  async function resetTest() {
    setBusy(true); setError('');
    try {
      await api('/preview-reset', 'POST', {});
      localStorage.removeItem(key);
      localStorage.removeItem(key + '-order');
      setEntries([]); setOrder(null); setAccess(null); setQuote(null);
      setCode(''); setCandidate(null); setConfirmed(false);
    } catch (failure) { setError((failure as Error).message); }
    finally { setBusy(false); }
  }

  return <div className="gc-page">
    {preview && <div className="gc-preview">LOCAL PREVIEW · No charges or real SIM activations
      <button onClick={resetTest} disabled={busy}>Reset test</button></div>}
    <main className="gc-main">
      <header className="gc-header">
        <img src={brand.logo} alt={brand.displayName} />
        <div><h1>Add SIMs to your plan</h1><p>Keep your cameras on one shared plan.</p></div>
      </header>
      {!enabled ? <section className="gc-card"><p>Adding SIMs to an existing plan is being prepared.</p>
        <a href="/dashboard">Back to your cameras →</a></section>
      : !signedIn ? <section className="gc-card"><h2>Sign in first</h2>
        <p>We’ll check your current plan before showing a price. Your email alone cannot change your account.</p>
        <a className="gc-primary" href="/login?redirect=%2Factivate%2Fgroup%2Fadd">Sign in to continue →</a>
      </section> : <>
        {error && <div className="gc-error" role="alert">{error}
          {wrongAccount && <p><a href="/login?redirect=%2Factivate%2Fgroup%2Fadd&switch=1">Sign in with the other email →</a></p>}
        </div>}
        {!account && !error && <div className="gc-card" role="status">Checking your current plan…</div>}
        {account && !order && <section className="gc-card">
          <div className="gc-card-heading"><h2>Your current plan</h2><span>{account.plan.name} · {account.plan.interval === 'year' ? 'Yearly' : 'Monthly'}</span></div>
          <p>{account.members.length} SIM{account.members.length === 1 ? '' : 's'} already on this plan. New SIMs will share its data and renewal date.</p>
          <div className="gc-chips">{account.members.map(member => <span key={member.iccid}>
            {member.nickname || 'Camera'} · SIM ending {member.iccid.slice(-4)}
          </span>)}</div>
          {entries.length > 0 && <div className="gc-added"><h3>New SIMs</h3>{entries.map(sim =>
            <div key={sim.iccid}><span>{sim.code.length === 4 ? sim.code : `SIM ending ${sim.iccid.slice(-4)}`}</span>
              <button onClick={() => { setEntries(current => current.filter(item => item.iccid !== sim.iccid)); setQuote(null); }}>Remove</button></div>)}</div>}
          {entries.length < account.max_new_sims && !candidate && <form onSubmit={checkSim} className="gc-form">
            <div className="gc-modes" role="group" aria-label="SIM number format">
              <button type="button" aria-pressed={mode === 'code'} onClick={() => { setMode('code'); setCode(''); }}>4-letter code</button>
              <button type="button" aria-pressed={mode === 'iccid'} onClick={() => { setMode('iccid'); setCode(''); }}>Full SIM number</button>
            </div>
            <label htmlFor="gc-code">{entries.length ? 'Next SIM' : 'New SIM code'}</label>
            <div className="gc-input-row"><input id="gc-code" value={code} maxLength={mode === 'code' ? 4 : 20}
              inputMode={mode === 'iccid' ? 'numeric' : 'text'} autoComplete="off"
              placeholder={mode === 'code' ? 'e.g. FARM' : '19–20 digit ICCID'}
              onChange={event => { setCode(mode === 'code' ? event.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4)
                : event.target.value.replace(/\D/g, '').slice(0, 20)); setQuote(null); }} />
              <button type="submit" disabled={!valid || busy}>Check SIM</button></div>
            <small>{mode === 'code' ? `${code.length} of 4 letters` : `${code.length} of 19–20 digits`}</small>
          </form>}
          {candidate && <div className="gc-confirm"><h3>Is this your SIM?</h3>
            <p>Check the last four digits on the SIM card or camera.</p>
            <strong>{candidate.iccid.slice(-4)}</strong>
            <label><input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} /> Yes, my SIM ends in {candidate.iccid.slice(-4)}</label>
            <div><button className="gc-primary" onClick={addSim} disabled={!confirmed}>Add this SIM</button>
              <button className="gc-text" onClick={() => { setCandidate(null); setCode(''); }}>Try another code</button></div>
          </div>}
          {entries.length > 0 && <button className="gc-primary gc-quote-button" onClick={checkPrice} disabled={busy}>
            {busy ? 'Checking your price…' : 'Review combined plan →'}</button>}
        </section>}
        {quote && !order && <section className="gc-card gc-quote" aria-live="polite"><h2>Your combined plan</h2>
          <p><strong>{quote.combined_count} SIMs</strong> share <strong>{quote.combined_data_gb} GB per month</strong>.</p>
          <div><span>Price for all SIMs at renewal</span><strong>{formatMoney(quote.renewal_total)} / {quote.plan.interval}</strong></div>
          <p className="gc-note">Your existing renewal date stays the same. Changes and pauses apply to the whole group.</p>
          {preview && <button className="gc-primary" onClick={simulateCheckout} disabled={busy}>
            {busy ? 'Preparing test checkout…' : 'Continue to simulated payment →'}</button>}
          {!preview && <p className="gc-hold">Checkout for existing plans is still in development. No payment or activation has happened.</p>}
        </section>}
        {order && <section className="gc-card gc-quote" aria-live="polite">
          {order.status === 'checkout' ? <><h2>Simulated payment</h2>
            <p>The customer and subscription stay the same. This is a local test with no bank charge.</p>
            <div><span>Prorated amount today</span><strong>{formatMoney(order.conversion_due_now || 0)}</strong></div>
            <p>All {order.items.length} SIMs will renew together on the existing date.</p>
            <button className="gc-primary" disabled={busy} onClick={() => simulatePayment('success')}>Simulate successful payment</button>
            <button className="gc-text" disabled={busy} onClick={() => simulatePayment('decline')}>Simulate declined payment</button>
            <button className="gc-text" disabled={busy} onClick={backFromPayment}>← Edit SIMs</button>
          </> : <><h2>{order.status === 'complete' ? 'Your SIMs are activated' : 'Activating your new SIMs'}</h2>
            <p>Your existing cameras stay connected. We only activate the newly added SIMs.</p>
            <div className="gc-progress">{order.items.map((item, index) => <p key={item.iccid}>
              <strong>{index < (account?.members.length || 0) ? 'Existing' : 'New'} SIM ending {item.iccid.slice(-4)}</strong>
              <span>{item.state === 'active' ? '✓ Active' : item.message || 'Activating…'}</span>
            </p>)}</div>
          </>}
        </section>}
      </>}
    </main>
  </div>;
}
